import { mensagemDoErroDeCarga } from '../design';

/**
 * Os números financeiros do menu, da faixa do topo das telas financeiras e de "Recebimentos do Mês" (08/10/2026).
 *
 * São lidos a cada 5 minutos, de quatro rotas (faturas, resumo de recebíveis, painel fiscal, resumo de despesas), com
 * `allSettled` de propósito: um 403 numa não derruba as outras. O defeito era o resto: cada métrica caía em `?? 0` e o
 * estado era sobrescrito SEMPRE. Uma recarga que falhava (um deploy do backend dura minutos) trocava os números certos
 * por ZERO: "Recebimentos do Mês R$ 0,00", e os badges de vencidas, boletos pendentes, erros fiscais e despesas
 * vencidas SUMIAM do menu (badge 0 não aparece). O alarme se calava justamente durante o deploy.
 *
 * Agora cada fonte responde por si: a que leu atualiza os seus números; a que falhou MANTÉM o último valor bom e diz o
 * motivo; o que nunca foi lido é `null` (a tela mostra "—" e o menu, nenhum número).
 */
export type Fonte = 'faturas' | 'resumo' | 'fiscal' | 'despesas';

export interface BadgesFinanceiros {
  drafts: number | null;
  boletosPending: number | null;
  overdue: number | null;
  totalOpen: number | null;
  totalReceived: number | null;
  totalOverdue: number | null;
  receivedCount: number | null;
  byPaymentMethod: Record<string, { count: number; open: number; paid: number }> | null;
  recentPayments: any[] | null;
  fiscalErrors: number | null;
  expOverdue: number | null;
}

/** Por fonte: quando leu por último (ISO) e, se a última tentativa falhou, o motivo. */
export type EstadoDasFontes = Record<Fonte, { lidoEm: string | null; erro: string | null }>;

export const BADGES_SEM_LEITURA: BadgesFinanceiros = {
  drafts: null, boletosPending: null, overdue: null, totalOpen: null, totalReceived: null, totalOverdue: null,
  receivedCount: null, byPaymentMethod: null, recentPayments: null, fiscalErrors: null, expOverdue: null,
};
export const FONTES_SEM_LEITURA: EstadoDasFontes = {
  faturas: { lidoEm: null, erro: null }, resumo: { lidoEm: null, erro: null },
  fiscal: { lidoEm: null, erro: null }, despesas: { lidoEm: null, erro: null },
};

export interface LeituraDosBadges {
  faturas: PromiseSettledResult<any>;
  /**
   * Ausente = não se aplica: quem não é admin não pede os totais (23/10/2026, totais só admin). A fonte fica como está,
   * nem "lida" nem "falhou", e os números dela seguem "—", nunca 0.
   */
  resumo?: PromiseSettledResult<any>;
  fiscal: PromiseSettledResult<any>;
  despesas: PromiseSettledResult<any>;
}

/**
 * Aplica uma rodada de leitura sobre o estado anterior. Pura: a mesma entrada dá a mesma saída, e o teste a exercita
 * sem tela. `mes` é 'YYYY-MM' (o mês dos rascunhos e dos boletos pendentes); `agora` é ISO.
 */
export function aplicarLeitura(
  anterior: BadgesFinanceiros,
  fontes: EstadoDasFontes,
  leitura: LeituraDosBadges,
  mes: string,
  agora: string,
): { badges: BadgesFinanceiros; fontes: EstadoDasFontes } {
  const badges: BadgesFinanceiros = { ...anterior };
  const novas: EstadoDasFontes = { ...fontes };
  const falhou = (f: Fonte, r: PromiseRejectedResult) => { novas[f] = { lidoEm: fontes[f].lidoEm, erro: mensagemDoErroDeCarga(r.reason) }; };
  const leu = (f: Fonte) => { novas[f] = { lidoEm: agora, erro: null }; };

  if (leitura.faturas.status === 'fulfilled') {
    const invoices = Array.isArray(leitura.faturas.value) ? leitura.faturas.value : [];
    const doMes = invoices.filter((i: any) => i.referenceMonth === mes);
    badges.drafts = doMes.filter((i: any) => i.status === 'Rascunho').length;
    // `paymentMethod` no banco é minúsculo ('boleto'): com 'Boleto' este contador vivia zerado.
    badges.boletosPending = doMes.filter(
      (i: any) => i.status === 'Aberta' && String(i.paymentMethod || '').toLowerCase() === 'boleto' && !i.nossoNumero,
    ).length;
    leu('faturas');
  } else falhou('faturas', leitura.faturas);

  if (!leitura.resumo) {
    // não se aplica (ver LeituraDosBadges.resumo)
  } else if (leitura.resumo.status === 'fulfilled') {
    const s: any = leitura.resumo.value || {};
    badges.overdue = s.counts?.overdue ?? 0;
    badges.totalOpen = s.totals?.totalOpen ?? 0;
    badges.totalReceived = s.totals?.totalReceived ?? 0;
    badges.totalOverdue = s.totals?.totalOverdue ?? 0;
    badges.receivedCount = s.counts?.received ?? 0;
    badges.byPaymentMethod = s.byPaymentMethod ?? {};
    badges.recentPayments = s.recentPayments ?? [];
    leu('resumo');
  } else falhou('resumo', leitura.resumo as PromiseRejectedResult);

  if (leitura.fiscal.status === 'fulfilled') {
    const f: any = leitura.fiscal.value || {};
    badges.fiscalErrors = f.errorCount ?? f.errors ?? 0;
    leu('fiscal');
  } else falhou('fiscal', leitura.fiscal);

  if (leitura.despesas.status === 'fulfilled') {
    const porStatus = (leitura.despesas.value as any)?.byStatus || [];
    badges.expOverdue = porStatus
      .filter((x: any) => x.status === 'Vencido')
      .reduce((soma: number, x: any) => soma + (parseInt(x.count) || 0), 0);
    leu('despesas');
  } else falhou('despesas', leitura.despesas);

  return { badges, fontes: novas };
}
